import { PermissionsMapEnum } from '@/_types/permissions';

const PERMISSIONS_DATA_BASE: Record<
  PermissionsMapEnum,
  {
    name: string;
    description: string;
    isEnabled: boolean;
    isAlwaysEnabled?: boolean;
    category: string;
  }
> = {
  // Company permissions (900-999)
  [PermissionsMapEnum['company.add']]: {
    name: 'Entity Add',
    description: 'Allows adding new entities to the firm',
    isEnabled: true,
    category: 'company',
  },
  [PermissionsMapEnum['company.edit']]: {
    name: 'Entity Edit',
    description: 'Allows editing entity details and information',
    isEnabled: true,
    category: 'company',
  },
  [PermissionsMapEnum['company.delete']]: {
    name: 'Entity Delete',
    description: 'Allows deleting entities from the firm',
    isEnabled: true,
    category: 'company',
  },
  [PermissionsMapEnum['company.view']]: {
    name: 'Entity View',
    description: 'Allows viewing information of assigned entities',
    isEnabled: true,
    isAlwaysEnabled: true,
    category: 'company',
  },
  [PermissionsMapEnum['company.edit_assignee']]: {
    name: 'Edit Entity Assignee',
    description: 'Allows changing which advisor is assigned to an entity',
    isEnabled: true,
    category: 'company',
  },

  // Conversation permissions (200-299)
  [PermissionsMapEnum['conversation.add']]: {
    name: 'Conversation Add',
    description: 'Allows creating new conversations with clients',
    isEnabled: false,
    category: 'conversation',
  },
  [PermissionsMapEnum['conversation.edit']]: {
    name: 'Conversation Edit',
    description: 'Allows editing conversation messages and details',
    isEnabled: false,
    category: 'conversation',
  },
  [PermissionsMapEnum['conversation.delete']]: {
    name: 'Conversation Delete',
    description: 'Allows deleting conversations and messages',
    isEnabled: false,
    category: 'conversation',
  },
  [PermissionsMapEnum['conversation.view']]: {
    name: 'Conversation View',
    description:
      'Allows viewing conversations and messages from assigned entities',
    isEnabled: true,
    isAlwaysEnabled: true,
    category: 'conversation',
  },
  [PermissionsMapEnum['conversation.send_mass_email']]: {
    name: 'Conversation Send Mass Email',
    description: 'Allows sending bulk emails to multiple clients',
    isEnabled: false,
    category: 'conversation',
  },

  // Document permissions (300-399)
  [PermissionsMapEnum['document.add']]: {
    name: 'Document Add',
    description: 'Allows uploading new documents to the system',
    isEnabled: true,
    category: 'document',
  },
  [PermissionsMapEnum['document.edit']]: {
    name: 'Document Edit',
    description: 'Allows editing document details and metadata',
    isEnabled: true,
    category: 'document',
  },
  [PermissionsMapEnum['document.delete']]: {
    name: 'Document Delete',
    description: 'Allows deleting documents from the system',
    isEnabled: true,
    category: 'document',
  },
  [PermissionsMapEnum['document.view']]: {
    name: 'Document View',
    description: 'Allows viewing documents and their details',
    isEnabled: true,
    isAlwaysEnabled: true,
    category: 'document',
  },
  [PermissionsMapEnum['document.edit_tags']]: {
    name: 'Document Edit Tags',
    description: 'Allows editing document tags and categories',
    isEnabled: true,
    category: 'document',
  },
  [PermissionsMapEnum['document.edit_rename']]: {
    name: 'Document Rename',
    description: 'Allows renaming documents in the system',
    isEnabled: true,
    category: 'document',
  },
  [PermissionsMapEnum['document.move']]: {
    name: 'Document Move',
    description: 'Allows moving documents between folders or locations',
    isEnabled: true,
    category: 'document',
  },
  [PermissionsMapEnum['document.copy']]: {
    name: 'Document Copy',
    description: 'Allows copying documents within the system',
    isEnabled: true,
    category: 'document',
  },

  // Firm permissions (800-899)
  [PermissionsMapEnum['firm.settings']]: {
    name: 'Firm Settings',
    description: 'Allows managing firm-wide settings and configurations',
    isEnabled: true,
    category: 'firm',
  },
  [PermissionsMapEnum['firm.billing']]: {
    name: 'Firm Billing',
    description: 'Allows managing firm billing and payment information',
    isEnabled: true,
    category: 'firm',
  },
  [PermissionsMapEnum['firm.client_import']]: {
    name: 'Firm Client Import',
    description: 'Allows importing clients from external systems',
    isEnabled: true,
    category: 'firm',
  },
  [PermissionsMapEnum['firm.edit_firm_roles']]: {
    name: 'Firm Roles',
    description: 'Allows editing firm roles and permissions',
    isEnabled: true,
    category: 'firm',
  },

  // Questionnaire permissions (400-499)
  [PermissionsMapEnum['questionnaire.add']]: {
    name: 'Questionnaire Add',
    description: 'Allows creating new questionnaires for clients',
    isEnabled: true,
    category: 'questionnaire',
  },
  [PermissionsMapEnum['questionnaire.edit']]: {
    name: 'Questionnaire Edit',
    description: 'Allows editing questionnaire content and settings',
    isEnabled: true,
    category: 'questionnaire',
  },
  [PermissionsMapEnum['questionnaire.delete']]: {
    name: 'Questionnaire Delete',
    description: 'Allows deleting questionnaires from the system',
    isEnabled: true,
    category: 'questionnaire',
  },
  [PermissionsMapEnum['questionnaire.view']]: {
    name: 'Questionnaire View',
    description:
      'Allows viewing questionnaires from assigned entities and their responses',
    isEnabled: true,
    isAlwaysEnabled: true,
    category: 'questionnaire',
  },

  // Questionnaire template permissions (500-599)
  [PermissionsMapEnum['questionnaire_template.add']]: {
    name: 'Questionnaire Template Add',
    description: 'Allows creating new questionnaire templates',
    isEnabled: false,
    category: 'questionnaire_template',
  },
  [PermissionsMapEnum['questionnaire_template.edit']]: {
    name: 'Questionnaire Template Edit',
    description: 'Allows editing questionnaire template content',
    isEnabled: false,
    category: 'questionnaire_template',
  },
  [PermissionsMapEnum['questionnaire_template.delete']]: {
    name: 'Questionnaire Template Delete',
    description: 'Allows deleting questionnaire templates',
    isEnabled: false,
    category: 'questionnaire_template',
  },
  [PermissionsMapEnum['questionnaire_template.view']]: {
    name: 'Questionnaire Template View',
    description: 'Allows viewing questionnaire templates',
    isEnabled: false,
    category: 'questionnaire_template',
  },
  [PermissionsMapEnum['questionnaire_template.edit_template_editor_messages']]:
    {
      name: 'Questionnaire Template Edit Messages',
      description: 'Allows editing message templates used in questionnaires',
      isEnabled: false,
      category: 'questionnaire_template',
    },

  // Review and export permissions (700-799)
  [PermissionsMapEnum['review_and_export.add']]: {
    name: 'Review and Export Add',
    description: 'Allows adding new review and export tasks',
    isEnabled: true,
    category: 'review_and_export',
  },
  [PermissionsMapEnum['review_and_export.edit']]: {
    name: 'Review and Export Edit',
    description: 'Allows editing review and export data',
    isEnabled: true,
    category: 'review_and_export',
  },
  [PermissionsMapEnum['review_and_export.delete']]: {
    name: 'Review and Export Delete',
    description: 'Allows deleting review and export tasks',
    isEnabled: true,
    category: 'review_and_export',
  },
  [PermissionsMapEnum['review_and_export.view']]: {
    name: 'Review and Export View',
    description: 'Allows viewing review and export data and tasks',
    isEnabled: true,
    category: 'review_and_export',
  },

  // Tax return delivery permissions (600-699)
  [PermissionsMapEnum['tax_return_delivery.add']]: {
    name: 'Tax Return Delivery Add',
    description: 'Allows creating new tax return deliveries',
    isEnabled: true,
    category: 'tax_return_delivery',
  },
  [PermissionsMapEnum['tax_return_delivery.edit']]: {
    name: 'Tax Return Delivery Edit',
    description: 'Allows editing tax return delivery details',
    isEnabled: true,
    category: 'tax_return_delivery',
  },
  [PermissionsMapEnum['tax_return_delivery.delete']]: {
    name: 'Tax Return Delivery Delete',
    description: 'Allows deleting tax return deliveries',
    isEnabled: true,
    category: 'tax_return_delivery',
  },
  [PermissionsMapEnum['tax_return_delivery.view']]: {
    name: 'Tax Return Delivery View',
    description: 'Allows viewing tax return delivery information',
    isEnabled: true,
    category: 'tax_return_delivery',
  },
  [PermissionsMapEnum['tax_return_delivery.mark_paid']]: {
    name: 'Tax Return Delivery Mark Paid',
    description: 'Allows marking tax returns as paid in the system',
    isEnabled: true,
    category: 'tax_return_delivery',
  },
  [PermissionsMapEnum['tax_return_delivery.send']]: {
    name: 'Tax Return Delivery Send',
    description: 'Allows sending completed tax returns to clients',
    isEnabled: true,
    category: 'tax_return_delivery',
  },

  // User permissions (100-199)
  [PermissionsMapEnum['user.add']]: {
    name: 'Add Client User',
    description: 'Allows adding new clients to the firm',
    isEnabled: true,
    category: 'user',
  },
  [PermissionsMapEnum['user.edit']]: {
    name: 'Edit Client User',
    description: 'Allows editing client details and information',
    isEnabled: true,
    category: 'user',
  },
  [PermissionsMapEnum['user.delete']]: {
    name: 'Delete Client User',
    description: 'Allows deleting clients from the firm',
    isEnabled: true,
    category: 'user',
  },
  [PermissionsMapEnum['user.view']]: {
    name: 'View Client User',
    description: 'Allows viewing client information and profiles',
    isEnabled: true,
    isAlwaysEnabled: true,
    category: 'user',
  },
  [PermissionsMapEnum['user.edit_assignee']]: {
    name: 'Edit Client User Assignee',
    description: 'Allows changing client assignments and responsibilities',
    isEnabled: false,
    category: 'user',
  },
};

export const PERMISSION_CATEGORIES: Record<string, string> = {
  company: 'Entity Management',
  user: 'User Management',
  conversation: 'Communication',
  document: 'Document Management',
  questionnaire: 'Questionnaire Management',
  questionnaire_template: 'Questionnaire Templates',
  review_and_export: 'Review & Export',
  tax_return_delivery: 'Tax Return Delivery',
  firm: 'Firm Settings',
};

// Backend access levels from permission.rb
export const ACCESS_BASIC = [
  'company.edit',
  'company.view',
  'conversation.view',
  'document.add',
  'document.copy',
  'document.delete',
  'document.edit',
  'document.edit_rename',
  'document.edit_tags',
  'document.move',
  'document.view',
  'questionnaire.add',
  'questionnaire.delete',
  'questionnaire.edit',
  'questionnaire.view',
  'review_and_export.add',
  'review_and_export.delete',
  'review_and_export.edit',
  'review_and_export.view',
  'tax_return_delivery.add',
  'tax_return_delivery.delete',
  'tax_return_delivery.edit',
  'tax_return_delivery.mark_paid',
  'tax_return_delivery.send',
  'tax_return_delivery.view',
  'user.view',
];

export const ACCESS_GROUP = [
  ...ACCESS_BASIC,
  'company.add',
  'company.delete',
  'company.edit',
  'questionnaire_template.add',
  'questionnaire_template.delete',
  'questionnaire_template.edit_template_editor_messages',
  'questionnaire_template.edit',
  'questionnaire_template.view',
  'user.add',
  'user.delete',
  'user.edit',
];

export const ACCESS_FULL = [
  ...ACCESS_GROUP,
  'user.edit_assignee',
  'company.edit_assignee',
];

// ACCESS_PARTNER includes all permissions
export const ACCESS_PARTNER = Object.keys(PermissionsMapEnum).filter((key) =>
  isNaN(Number(key)),
);

export const PERMISSIONS_DATA = Object.fromEntries(
  Object.entries(PERMISSIONS_DATA_BASE).map(([key, permission]) => {
    const permissionKey = Object.keys(PermissionsMapEnum).find(
      (k) =>
        PermissionsMapEnum[k as keyof typeof PermissionsMapEnum] ===
        Number(key),
    );

    return [
      key,
      {
        ...permission,
        isAlwaysEnabled:
          permission.isAlwaysEnabled ||
          ACCESS_BASIC.includes(permissionKey || ''),
      },
    ];
  }),
) as Record<
  PermissionsMapEnum,
  {
    name: string;
    description: string;
    isEnabled: boolean;
    isAlwaysEnabled?: boolean;
    category: string;
  }
>;

export const DEFAULT_FIRM_ROLES = [
  {
    role_name: 'Client Manager',
    permission_keys: [
      'company.add',
      'company.edit',
      'company.delete',
      'company.view',
      'company.edit_assignee',
      'user.add',
      'user.edit',
      'user.delete',
      'user.view',
      'user.edit_assignee',
    ],
  },
  {
    role_name: 'Preparer',
    permission_keys: [
      'tax_return_delivery.add',
      'tax_return_delivery.edit',
      'tax_return_delivery.delete',
      'tax_return_delivery.view',
      'tax_return_delivery.mark_paid',
      'tax_return_delivery.send',
      'review_and_export.add',
      'review_and_export.edit',
      'review_and_export.delete',
      'review_and_export.view',
    ],
  },
  {
    role_name: 'Reviewer',
    permission_keys: [
      'review_and_export.add',
      'review_and_export.edit',
      'review_and_export.delete',
      'review_and_export.view',
    ],
  },
];
